import { Box, Button, Grid, IconButton, Tooltip } from '@material-ui/core';
import NumericUpDown from '../atom/NumericUpDown';
import { formatMoney } from '../../utils';
import { convertToFinalPrice } from '../../finalPrice';
import moment from 'moment';
import { EyeIcon, EyeSlashIcon } from '../../icons';
import ticketAvailabilityInfo from './ticketAvailabilityInfo';
import useTicketListVisibility from '../../hooks/useTicketVisibility';
import { isTicketLongExpired } from '../../utils/ticketHelper';

const TicketList = ({
    tickets,
    startTime,
    role,
    ticketCounts,
    setTicketCounts,
    updateTickets = (ticketId, newValue) => {}
}) => {
    const {
        hideUnavailable,
        setHideUnavailable,
        showLongExpired,
        setShowLongExpired,
        visibleTickets
    } = useTicketListVisibility({
        tickets,
        role,
        startTime
    });

    const handleToggleUnavailable = () => {
        if (!hideUnavailable) {
            setShowLongExpired(false);
        }

        setHideUnavailable(!hideUnavailable);
    };

    const handleChange = ({ value, ticket }) => {
        const newW = parseInt(value, 10);
        if (Number.isNaN(newW) || newW < 0) {
            return;
        }
        setTicketCounts(ticketCounts.map(e => (e.id === ticket.id ? { ...e, value: newW } : e)));
        updateTickets?.(ticket.id, newW);
    };

    const hasLongExpiredTickets =
        role === 'agent' &&
        !hideUnavailable &&
        tickets.some(ticket => isTicketLongExpired(ticket, startTime));

    return (
        <>
            {role === 'agent' && (
                <Box
                    style={{
                        width: '100%',
                        margin: '4px 0 12px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                    }}
                >
                    <Tooltip
                        title={
                            hideUnavailable
                                ? 'Show unavailable tickets'
                                : 'Hide unavailable tickets'
                        }
                        placement="right"
                    >
                        <IconButton
                            onClick={handleToggleUnavailable}
                            size="small"
                            style={{ padding: 6 }}
                        >
                            {hideUnavailable ? <EyeIcon /> : <EyeSlashIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {visibleTickets.map(ticket => {
                const ticketCount = ticketCounts.find(e => e.id === ticket.id);
                if (!ticketCount) {
                    return null;
                }

                const { available, notYetValid, expired } = ticketAvailabilityInfo(
                    ticket,
                    startTime
                );

                const statusSuffix = notYetValid
                    ? ` (available from: ${moment(ticket.date_from).format('ddd, MMM DD h:mm')})`
                    : expired
                    ? ` (expired: ${moment(ticket.date_to).format('ddd, MMM DD h:mm')})`
                    : '';

                return (
                    <Grid
                        item
                        xs={12}
                        key={ticket.id}
                        style={{ color: available ? 'inherit' : 'red' }}
                    >
                        <NumericUpDown
                            label={`${formatMoney(
                                convertToFinalPrice(ticket.retail_price, ticket?.final_price)
                            )} - ${ticket.name}${statusSuffix}`}
                            value={ticketCount.value}
                            onChange={(_, value) => handleChange({ value, ticket })}
                        />
                    </Grid>
                );
            })}

            {hasLongExpiredTickets && (
                <Box
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        marginTop: 8
                    }}
                >
                    <Button
                        size="small"
                        color="primary"
                        onClick={() => setShowLongExpired(!showLongExpired)}
                    >
                        {showLongExpired
                            ? 'Hide tickets expired more than 3 months ago'
                            : 'Show tickets expired more than 3 months ago'}
                    </Button>
                </Box>
            )}
        </>
    );
};

export default TicketList;
